import { AuctionSecurity } from '@/modules/common/models';
import { ColDef, ValueFormatterParams } from 'ag-grid-enterprise';
import {
  alignCenter,
  alignLeft,
  component,
} from '@/modules/common/components/ag-table/columns/utils';
import { CustomDataType } from '@/modules/common/components/ag-table/columns/common';
import { formatSecurityCategoryString, formatSecurityTypeString } from '@/connect';
import { h } from 'vue';
import { VIcon } from 'vuetify/lib';
import PriceOutput from '@/modules/common/components/format-price/PriceOutput.vue';
import RateOutput from '@/modules/common/components/format-rate/RateOutput.vue';
import Decimal from 'decimal.js';

type BooleanFields = {
  [K in keyof AuctionSecurity]: AuctionSecurity[K] extends boolean ? K : never;
}[keyof AuctionSecurity];

// Read-only checkbox icon renderer
const readOnlyCheckboxAdapter = component<{ checked: boolean }>(
  (props) => () =>
    h(
      VIcon,
      { props: { color: 'grey' } },
      props.checked ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-off-outline'
    )
);

// Editable checkbox renderer
export const editableCheckboxAdapter = component<{
  checked: boolean;
  onChange: (value: boolean) => void;
}>(
  (props) => () =>
    h(
      VIcon,
      {
        props: { color: props.checked ? 'green' : 'grey' },
        style: { cursor: 'pointer' },
        on: { click: () => props.onChange(!props.checked) },
      },
      props.checked ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'
    )
);

function readOnlyCheckboxColumn(field: BooleanFields, headerName: string): ColDef<AuctionSecurity> {
  return {
    field,
    colId: field,
    headerName,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    cellRendererSelector: (params) => readOnlyCheckboxAdapter({ checked: params.value ?? false }),
    ...alignCenter(),
  };
}

export function ticker(): ColDef<AuctionSecurity> {
  return {
    field: 'ticker',
    colId: 'ticker',
    headerName: 'Ticker',
    ...alignLeft(),
  };
}

export function cusip(): ColDef<AuctionSecurity> {
  return {
    field: 'cusip',
    colId: 'cusip',
    headerName: 'CUSIP',
    cellDataType: CustomDataType.CUSIP,
    ...alignLeft(),
  };
}

export function enabled(onToggle: (security: AuctionSecurity) => void): ColDef<AuctionSecurity> {
  return {
    field: 'isAuroraActive',
    colId: 'isAuroraActive',
    headerName: 'Enabled',
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    cellRendererSelector: (params) =>
      editableCheckboxAdapter({
        checked: params.value ?? false,
        onChange: () => {
          if (params.data) {
            params.data.isAuroraActive = !params.data.isAuroraActive;
            onToggle(params.data);
          }
        },
      }),
    ...alignCenter(),
  };
}

export function nsccNewEligible(): ColDef<AuctionSecurity> {
  return readOnlyCheckboxColumn('nsccNewEligible', 'NSCC New Eligible');
}

export function nsccRollEligible(): ColDef<AuctionSecurity> {
  return readOnlyCheckboxColumn('nsccRollEligible', 'NSCC Roll Eligible');
}

export function occEligible(): ColDef<AuctionSecurity> {
  return readOnlyCheckboxColumn('occEligible', 'OCC Eligible');
}

export function restricted(onToggle: (security: AuctionSecurity) => void): ColDef<AuctionSecurity> {
  return {
    field: 'isAuroraRestricted',
    colId: 'isAuroraRestricted',
    headerName: 'Restricted',
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderFilterButton: true,
    cellRendererSelector: (params) =>
      editableCheckboxAdapter({
        checked: params.value ?? false,
        onChange: () => {
          if (params.data) {
            params.data.isAuroraRestricted = !params.data.isAuroraRestricted;
            onToggle(params.data);
          }
        },
      }),
    ...alignCenter(),
  };
}

export function marketplaceEligible(): ColDef<AuctionSecurity> {
  return readOnlyCheckboxColumn('marketplaceEligible', 'Marketplace Eligible');
}

export function tradeManagementEligible(): ColDef<AuctionSecurity> {
  return readOnlyCheckboxColumn('tradeManagementEligible', 'Trade Management Eligible');
}

export function securityType(): ColDef<AuctionSecurity> {
  return {
    field: 'securityType',
    colId: 'securityType',
    headerName: 'Security Type',
    ...alignLeft(),
    valueFormatter: (params: ValueFormatterParams<AuctionSecurity>) => {
      return params.data ? formatSecurityTypeString(params.data.securityType) : '–';
    },
  };
}

export function securityCategory(): ColDef<AuctionSecurity> {
  return {
    field: 'securityCategory',
    colId: 'securityCategory',
    headerName: 'Security Category',
    ...alignLeft(),
    valueFormatter: (params: ValueFormatterParams<AuctionSecurity>) => {
      return params.data ? formatSecurityCategoryString(params.data.securityCategory) : '–';
    },
  };
}

const priceOutputAdapter = component<{ price: Decimal }>(
  (props) => () => h(PriceOutput, { props: { price: props.price } })
);

const rateOutputAdapter = component<{ rate: Decimal }>(
  (props) => () => h(RateOutput, { props: { rate: props.rate.mul(100) } })
);

const naTextAdapter = component<Record<string, never>>(
  () => () => h('pre', { class: 'px-10' }, 'N/A')
);

export function lastClosePrice(): ColDef<AuctionSecurity> {
  return {
    field: 'closePrice',
    colId: 'lastClosePrice',
    headerName: 'Last Close Price',
    cellRendererSelector: (params) => {
      if (params.data?.hasActivePriceDataSource && !params.data.closePrice.isZero()) {
        return priceOutputAdapter({ price: params.data.closePrice });
      }
      return naTextAdapter({});
    },
    ...alignLeft(),
  };
}

export function isinCode(): ColDef<AuctionSecurity> {
  return {
    field: 'isinCode',
    colId: 'isinCode',
    headerName: 'ISIN',
    valueFormatter: (params: ValueFormatterParams<AuctionSecurity>) => {
      return params.data?.isinCode ?? '–';
    },
    ...alignLeft(),
  };
}

export function security(): ColDef<AuctionSecurity> {
  return {
    field: 'name',
    colId: 'security',
    headerName: 'Security',
    valueFormatter: (params: ValueFormatterParams<AuctionSecurity>) => {
      if (params.data?.name === '') return '–';
      else return params.data?.name ?? '–';
    },
    ...alignLeft(),
  };
}

export function bondYield(): ColDef<AuctionSecurity> {
  return {
    field: 'yield',
    colId: 'yield',
    headerName: 'Bond Yield',
    ...alignCenter(),
    cellRendererSelector: (params) => {
      if (params.data?.yield) {
        return rateOutputAdapter({ rate: params.data.yield });
      }
      return naTextAdapter({});
    },
  };
}

export function securitySource(): ColDef<AuctionSecurity> {
  return {
    field: 'securitySource',
    colId: 'securitySource',
    headerName: 'Security Source',
    ...alignLeft(),
  };
}
